import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../../components/Navbar";
import QuizzList from "../../components/quizz/QuizzList";
import { AlertCircle } from "lucide-react";
import QuizResultModal from "../../components/quizz/QuizResultModal";
import QuizIntroModal from "../../components/quizz/QuizIntroModal";
import { motion } from "framer-motion";

const QuizzPlay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quiz = location.state?.quiz;

  const [showIntro, setShowIntro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);

  // ✅ Normalise user_choice_id pour toujours avoir un tableau [{id}]
  const normalizeChoices = (question) => {
    if (!Array.isArray(question.user_choice_id)) {
      if (question.user_choice_id == null) {
        question.user_choice_id = [];
      } else if (typeof question.user_choice_id === "number") {
        question.user_choice_id = [{ id: question.user_choice_id }];
      } else if (typeof question.user_choice_id === "string") {
        question.user_choice_id = [
          { id: parseInt(question.user_choice_id, 10) },
        ];
      }
    } else {
      // si c’est un tableau de nombres bruts [1,2], on mappe en [{id:1}, {id:2}]
      question.user_choice_id = question.user_choice_id.map((c) =>
        typeof c === "object" ? c : { id: c }
      );
    }
    return question;
  };

  // Vérification si le modal doit s'afficher
  useEffect(() => {
    const checkIntroStatus = async () => {
      try {
        const res = await axios.get("/api/v1/quiz/intro-status/", {
          headers: {
            Authorization: `JWT ${localStorage.getItem("access")}`,
          },
        });
        setShowIntro(res.data.show_intro);
      } catch (err) {
        // console.error("Erreur API intro-status :", err);
      } finally {
        setLoading(false); // on a fini de charger
      }
    };
    checkIntroStatus();
  }, []);

  const handleStartIntro = async () => {
    try {
      await axios.post(
        "/api/v1/quiz/intro-status/",
        {},
        {
          headers: {
            Authorization: `JWT ${localStorage.getItem("access")}`,
          },
        }
      );
      setShowIntro(false); // cacher le modal
    } catch (err) {
      setShowIntro(false); // fallback : cacher le modal quand même
    }
  };

  const handleQuizFinished = (finalScore, totalQuestions) => {
    setScore(finalScore);
    setTotal(totalQuestions);
    setIsFinished(true);
  };

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex-1 flex flex-col justify-center items-center"
        >
          {/* Icône animée en zoom/dezoom permanent */}
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          </motion.div>

          <p className="text-lg font-semibold mb-4">
            Aucune question trouvée pour ce quiz.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/quizz")}
            className="px-6 py-3 bg-[#FFA000] text-white rounded shadow-lg"
          >
            Retour à la sélection
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p>Chargement...</p>
        </div>
      ) : showIntro ? (
        <QuizIntroModal onStart={handleStartIntro} />
      ) : (
        <>
          <div className="flex-1 p-1 mx-auto">
            <h1 className="text-2xl font-bold mt-6 text-center">
              {quiz.title}
            </h1>
            <QuizzList quiz={quiz} onQuizFinished={handleQuizFinished} />
          </div>

          {isFinished && (
            <QuizResultModal
              score={score}
              total={total}
              handleReview={() => {
                const normalizedQuestions = quiz.questions.map((q) =>
                  normalizeChoices(q)
                );
                navigate(`/quizz/review/${quiz?.id}`, {
                  state: { questions: normalizedQuestions, quiz },
                });
              }}
              onNext={() => navigate("/quizz")}
              onHome={() => navigate("/accueil")}
            />
          )}
        </>
      )}
    </div>
  );
};

export default QuizzPlay;
